import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddShoppingCartOutlined,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  FilterAltOutlined,
  Search as SearchIcon,
  ShoppingCartCheckoutOutlined,
  ShoppingCartOutlined,
  YoutubeSearchedForOutlined,
} from '@mui/icons-material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  applyFilterSearch,
  clearFilterValues,
  resetApplySearch,
  selectShouldApplySearch,
  setPriceFilter,
  setViewType,
  toggleFilterBubble,
} from '../../features/filters/filterSlice';
import { makeStyles } from '@mui/styles';
import { Item, ItemRarity } from '../../models/item';
import config from '../../config/config.json';
import axios from 'axios';
import toast from 'react-hot-toast';
import { searchKeys, useSearchItems } from '../../features/search/searchSlice';
import { notifyCartItemAdded } from '../../utils/cartUtils';

import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import PaginationControls from '../../components/PaginationControls/PaginationControls';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import ActiveFilters from '../../components/ActiveFilters/ActiveFilters';
import styles from '../../assets/jss/material-kit-react/views/profilePage';
import ToolsIcon from 'components/ToolsIcon';
import { ItemActions } from '../../components/ItemActions';

import hardcoreOff from '../../assets/img/hardcore_off.png';
import ladderOn from '../../assets/img/ladder_on.png';
import ladderOff from '../../assets/img/ladder_off.png';
import hardcoreOn from '../../assets/img/hardcore_on.png';

const getColorFromRarity = (rarName?: string): string => {
  const textColorMap: Record<ItemRarity, string> = {
    WHITE: '#FFFFFF',
    SET: '#00FF00',
    MAGIC: '#4169E1',
    UNIQUE: '#A59263',
    CRAFTED: '#FFA800',
    RARE: '#FFFF00',
    NORMAL: '#FFFFFF',
    RUNEWORD: '#A59263',
    RUNE: '#A59263',
  };

  return textColorMap[rarName?.toUpperCase() as ItemRarity] || '#FFFFFF';
};

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 1100,
    '& .MuiTabs-root': {
      zIndex: 1200,
    },
    '& .MuiTab-root': {
      zIndex: 1200,
    },
    '& .MuiTabs-root + div': {
      marginTop: 0,
    },
  },
  mainCard: {
    padding: '20px',
    borderRadius: '6px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    marginBottom: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    border: '1px solid rgba(241, 198, 43, 0.3)',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    width: '100%',
    position: 'relative',
    border: '1px solid rgba(0,0,0,0.1)',
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: '8px',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
      alignItems: 'stretch',
      padding: theme.spacing(2),
    },
  },
  toolbarContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(1),
      width: '100%',
    },
  },
}));

// Custom hook to allow passing searchTerm directly without waiting for state updates
const useSearchItemsWithTerm = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Function to perform search with a specific term
  const searchWithTerm = React.useCallback(
    async (term: string) => {
      const sessionToken = localStorage.getItem('session_token');

      try {
        const response = await axios.post(
          `${config.backend_base_url}public/pss/search`,
          { search_term: term },
          { headers: { 'session-token': sessionToken } }
        );

        return response.data || [];
      } catch (error) {
        console.error('Error searching items:', error);
        toast.error(t('search.errors.searchFailed', 'Failed to search items. Please try again'));
        return [];
      }
    },
    [t]
  );

  // Return functions for performing search
  return {
    searchWithTerm,
    invalidateQueries: () => queryClient.invalidateQueries({ queryKey: ['items', 'search'] }),
  };
};

export default React.memo(function InstantShopV1(): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const location = useLocation();
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [oldCheck, setOldCheck] = useState('');
  const [loadingData, setLoadingData] = useState(false);
  const [currentItemPage, setCurrentItemPage] = useState(1);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [savedSearchFilter, setSavedSearchFilter] = useState('');
  const [itemQuantities, setItemQuantities] = useState<Record<number, number>>({});
  const [hasInitialLoadChecked, setHasInitialLoadChecked] = useState(false);
  const [recentlyAppliedFilters, setRecentlyAppliedFilters] = useState(false);

  // Get shouldApplySearch flag for FiltersBubble integration
  const shouldApplySearch = useSelector(selectShouldApplySearch);
  const searchTermFromRedux = useSelector((state: any) => state.filters.searchTerm);

  // Get the full filter state to check if filters are active
  const filterState = useSelector((state: any) => state.filters);

  // Use the search hook with manual execution mode - disable autoFetch completely
  const { data: itemData = [], isLoading } = useSearchItems(searchTerm, false, true);

  // Get custom search function
  const { searchWithTerm } = useSearchItemsWithTerm();

  // Create state for search results from manual search
  const [manualSearchResults, setManualSearchResults] = useState<Item[]>([]);

  // Compute grouped items and stock quantities
  const { groupedItems, stockMap } = React.useMemo(() => {
    const groupMap: Record<string, Item[]> = {};
    const stockMap: Record<string, number> = {};

    // Combine both data sources inside the useMemo callback
    const combinedData = [...itemData, ...manualSearchResults];

    // Use combinedData for processing
    for (const item of combinedData) {
      const key = [
        item.stats_description || '',
        item.ocr_string || '',
        item.rarity_id || '',
        item.ladder || '',
        item.hardcore || '',
        item.skin_image_path || '',
        item.fiat_price || '',
        item.fg_price || '',
        item.type_id || '',
      ].join('|');
      if (!groupMap[key]) groupMap[key] = [];
      groupMap[key].push(item);
      stockMap[key] = (stockMap[key] || 0) + 1;
    }
    // Only use the first item in each group for rendering
    const groupedItems = Object.values(groupMap).map((items) => items[0]);
    return { groupedItems, stockMap };
  }, [itemData, manualSearchResults]);

  // Refs
  const savedSearchesButtonRef = useRef<HTMLButtonElement>(null);

  // Selectors
  const isFilterBubbleOpen = useSelector((state: any) => state.filters.isFilterBubbleOpen);
  const savedSearches = useSelector((state: any) => state.filters.savedSearches || []);
  const [savedSearchesAnchorEl, setSavedSearchesAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );

  // Get filter states from Redux
  const { viewType, priceFilter } = useSelector((state: any) => state.filters);

  // Constants
  const itemsPerPage = 20;
  const isSavedSearchesOpen = Boolean(savedSearchesAnchorEl);

  // Style object for toolbar buttons
  const toolbarButtonSx = {
    backgroundColor: (theme) => theme.palette.background.default,
    color: (theme) => theme.palette.text.primary,
    border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
    '&:hover': {
      backgroundColor: (theme) => theme.palette.action.hover,
      border: (theme) => `1px solid ${theme.palette.secondary.main}`,
    },
    fontSize: { xs: 16, md: 18 },
    width: { xs: 28, md: 32 },
    height: { xs: 28, md: 32 },
    boxShadow: 0,
  };

  // Effects
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const qterm = queryParams.get('term') || '';
    setSearchTerm(qterm);
    setInputValue(qterm);
    setOldCheck(qterm);

    // Only trigger search if there's an actual search term from URL
    if (qterm.trim()) {
      // Immediately clear results and show loading for instant visual feedback
      setManualSearchResults([]);
      setLoadingData(true);

      // Perform immediate search for URL-based searches
      searchWithTerm(qterm)
        .then((results) => {
          setManualSearchResults(results);
          setLoadingData(false);
        })
        .catch((error) => {
          console.error('URL search error:', error);
          setLoadingData(false);
          toast.error(t('search.errors.searchFailed', 'Search failed. Please try again.'));
        });
    }

    // Mark initial load as checked
    setHasInitialLoadChecked(true);
  }, [location.search, searchWithTerm, t]);

  // Effect to handle initial page load with active filters (when navigated from other pages)
  useEffect(() => {
    // Only run this check after the initial load and give time for Redux state to be ready
    if (!hasInitialLoadChecked) return;

    // Small delay to ensure Redux state is fully loaded
    const timeoutId = setTimeout(() => {
      // Check if we have active filters but no search term or shouldApplySearch flag
      const hasActiveFilters =
        filterState.rarity !== '' ||
        filterState.type !== '' ||
        filterState.itemType !== '' ||
        filterState.newItems !== '' ||
        filterState.ethereal !== '' ||
        filterState.unidentified !== '' ||
        (filterState.excludeStatFilters && filterState.excludeStatFilters.length > 0) ||
        (filterState.statFilterGroups && filterState.statFilterGroups.length > 0);

      // Check if there's no current search term in URL
      const queryParams = new URLSearchParams(location.search);
      const urlSearchTerm = queryParams.get('term') || '';

      // Check if we already have results or are loading
      const hasExistingResults = manualSearchResults.length > 0 || loadingData;

      // If we have active filters but no search term, no pending search application, and no existing results
      if (
        hasActiveFilters &&
        !urlSearchTerm &&
        !shouldApplySearch &&
        !searchTermFromRedux &&
        !hasExistingResults
      ) {
        // Convert filters to search term and trigger search
        dispatch(applyFilterSearch());
      }
      // Only trigger default empty search if there are no results, not loading, no URL search term,
      // and we haven't recently applied filters (to avoid overriding filter results)
      else if (!urlSearchTerm && !hasExistingResults && !loadingData && !recentlyAppliedFilters) {
        // Trigger default empty search to show initial results
        setLoadingData(true);
        searchWithTerm('')
          .then((results) => {
            setManualSearchResults(results);
            setLoadingData(false);
          })
          .catch((error) => {
            console.error('Default search error:', error);
            setLoadingData(false);
          });
      }
    }, 100); // Reduced timeout to 100ms

    return () => clearTimeout(timeoutId);
  }, [
    hasInitialLoadChecked,
    filterState,
    shouldApplySearch,
    searchTermFromRedux,
    location.search,
    dispatch,
    manualSearchResults.length,
    loadingData,
    searchWithTerm,
    recentlyAppliedFilters,
  ]);

  // Effect to handle search requests from FiltersBubble
  useEffect(() => {
    if (shouldApplySearch) {
      // Get the search term from Redux (can be empty)
      const searchTermToUse = searchTermFromRedux || '';

      // Update input field with the generated search term
      setInputValue(searchTermToUse);
      setSearchTerm(searchTermToUse);

      // Immediately clear current results and show loading for instant visual feedback
      setManualSearchResults([]);
      setLoadingData(true);

      // Mark that we're applying filters
      setRecentlyAppliedFilters(true);

      // Update URL with the search term
      const params = new URLSearchParams(location.search);
      if (searchTermToUse) {
        params.set('term', searchTermToUse);
      } else {
        params.delete('term');
      }
      const newUrl = params.toString()
        ? `${location.pathname}?${params.toString()}`
        : location.pathname;
      window.history.pushState({}, '', newUrl);

      // Perform search with the generated term (even if empty)
      searchWithTerm(searchTermToUse)
        .then((results) => {
          setManualSearchResults(results);
          setLoadingData(false);

          if (results.length > 0) {
            toast.success(
              t('search.success.results', 'Search successful [{{count}}]', {
                count: results.length,
              })
            );
          } else {
            toast.error(t('search.errors.noMatches', 'Search found no matches'));
          }

          // Clear the flag after a short delay to prevent immediate default search
          setTimeout(() => {
            setRecentlyAppliedFilters(false);
          }, 500);
        })
        .catch((error) => {
          console.error('Search error:', error);
          setLoadingData(false);
          toast.error(t('search.errors.searchFailed', 'Search failed. Please try again.'));

          // Clear the flag even on error
          setTimeout(() => {
            setRecentlyAppliedFilters(false);
          }, 500);
        });

      // Reset the flag
      dispatch(resetApplySearch());
    }
  }, [
    shouldApplySearch,
    searchTermFromRedux,
    searchWithTerm,
    dispatch,
    t,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (oldCheck !== searchTerm) {
      setOldCheck(searchTerm);
    }
  }, [searchTerm, oldCheck]);

  // Update loadingData based on the query status
  useEffect(() => {
    setLoadingData(isLoading);
  }, [isLoading]);

  // Handlers
  const handleSavedSearchesClose = () => {
    setSavedSearchesAnchorEl(null);
  };

  const handleSavedSearchClick = async (searchTerm: string) => {
    // Update input field with the saved search term
    setInputValue(searchTerm);
    setSearchTerm(searchTerm);

    // Close the popover
    handleSavedSearchesClose();

    // Clear active filters since they may not be relevant for the saved search
    dispatch(clearFilterValues());

    // Immediately clear current results and show loading for instant visual feedback
    setManualSearchResults([]);
    setLoadingData(true);

    // Update URL with the search term
    const params = new URLSearchParams(location.search);
    if (searchTerm) {
      params.set('term', searchTerm);
    } else {
      params.delete('term');
    }
    const newUrl = params.toString()
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    window.history.pushState({}, '', newUrl);

    try {
      // Perform direct search with the saved search term
      const results = await searchWithTerm(searchTerm);
      setManualSearchResults(results);

      // Clear loading state
      setLoadingData(false);

      // Show success/error toast based on results
      if (results.length > 0) {
        toast.success(
          t('search.success.results', 'Search successful [{{count}}]', {
            count: results.length,
          })
        );
      } else {
        toast.error(t('search.errors.noMatches', 'Search found no matches'));
      }
    } catch (error) {
      // Clear loading state
      setLoadingData(false);
      console.error('Search error:', error);
      toast.error(t('search.errors.searchFailed', 'Search failed. Please try again.'));
    }
  };

  const handleAddToCart = (itemId: number) => {
    cartItem(new MouseEvent('click') as any, itemId);
  };

  const handleQuantityChange = (itemId: number, delta: number) => {
    setItemQuantities((q) => {
      const current = q[itemId] || 1;
      const next = Math.max(1, current + delta);
      return { ...q, [itemId]: next };
    });
  };

  // API calls
  const cartItem = async (event: React.MouseEvent, itemID: number) => {
    event.preventDefault();
    if (isNaN(itemID)) {
      toast.error(t('search.errors.invalidId', 'Invalid item ID'));
      return;
    }

    const quantity = itemQuantities[itemID] || 1;
    const loading = toast.loading(t('search.cart.adding', 'Adding item to cart'));

    try {
      // Find all items with the same grouping key to get the correct item IDs to cart
      const targetItem = [...itemData, ...manualSearchResults].find(
        (item) => Number(item.id) === itemID
      );
      if (!targetItem) {
        toast.error(t('search.errors.itemNotFound', 'Item not found'));
        return;
      }

      // Use a more comprehensive key with all important attributes
      const groupKey = [
        targetItem.stats_description || '',
        targetItem.ocr_string || '',
        targetItem.rarity_id || '',
        targetItem.ladder || '',
        targetItem.hardcore || '',
        targetItem.skin_image_path || '',
        targetItem.fiat_price || '',
        targetItem.fg_price || '',
        targetItem.type_id || '',
      ].join('|');

      // Get all items in the same group
      const allGroupItems = [...itemData, ...manualSearchResults].filter((item) => {
        const itemGroupKey = [
          item.stats_description || '',
          item.ocr_string || '',
          item.rarity_id || '',
          item.ladder || '',
          item.hardcore || '',
          item.skin_image_path || '',
          item.fiat_price || '',
          item.fg_price || '',
          item.type_id || '',
        ].join('|');
        return itemGroupKey === groupKey;
      });

      // Sort items so the representative item (first in display) is last to be removed
      // This ensures the card stays visible until all items are gone
      const representativeItem = allGroupItems[0]; // First item is the representative
      const sortedItems = [
        ...allGroupItems.filter((item) => item.id !== representativeItem.id), // Non-representative items first
        representativeItem, // Representative item last
      ];

      // Take only the requested quantity, prioritizing non-representative items
      const itemsToCart = sortedItems.slice(0, quantity);
      const itemIdsToCart = itemsToCart.map((item) => item.id);

      const response = await axios.post(
        `${config.backend_base_url}d2rs/cart/add`,
        { item_ids: itemIdsToCart },
        { headers: { 'session-token': sessionToken } }
      );

      if (response.status) {
        toast.success(t('search.cart.success', 'Successfully added item to cart'));

        // Remove only the specific items that were carted from search results
        setManualSearchResults((prevResults) =>
          prevResults.filter((item) => !itemIdsToCart.includes(item.id))
        );

        // Clear quantity state for this item
        setItemQuantities((prev) => {
          const updated = { ...prev };
          delete updated[itemID];
          return updated;
        });

        // Optimistically update TanStack Query cache to remove only the carted items
        if (searchTerm) {
          queryClient.setQueryData(searchKeys.search(searchTerm), (oldData: Item[] | undefined) => {
            if (!oldData) return oldData;
            return oldData.filter((item) => !itemIdsToCart.includes(item.id));
          });
        }

        // Invalidate search queries to refresh cached data
        queryClient.invalidateQueries({ queryKey: searchKeys.all });
        // Also invalidate any specific search term queries that might be cached
        if (searchTerm) {
          queryClient.invalidateQueries({ queryKey: searchKeys.search(searchTerm) });
        }

        // Notify components about cart update
        queryClient.invalidateQueries({ queryKey: ['cartItems'] });
        notifyCartItemAdded({ id: itemID });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error(t('search.errors.cartAddFailed', 'Failed to add item to cart'));
    } finally {
      toast.dismiss(loading);
    }
  };

  // Update search term handler
  const handleSearchChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleSearchClick = async () => {
    // Simple search - just search with whatever is in the input field
    const searchValue = inputValue.trim();

    // Allow empty searches - remove the validation check
    // if (!searchValue) {
    //   toast.error(t('search.errors.enterSearchTerm', 'Please enter a search term'));
    //   return;
    // }

    // Clear active filters since they may not be relevant for the new search
    dispatch(clearFilterValues());

    // Immediately clear current results and show loading for instant visual feedback
    setManualSearchResults([]);
    setLoadingData(true);

    // Update search term state
    setSearchTerm(searchValue);

    // Update URL with the search term
    const params = new URLSearchParams(location.search);
    if (searchValue) {
      params.set('term', searchValue);
    } else {
      params.delete('term');
    }
    const newUrl = params.toString()
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    window.history.pushState({}, '', newUrl);

    try {
      // Perform direct search with input value (even if empty)
      const results = await searchWithTerm(searchValue);
      setManualSearchResults(results);

      // Clear loading state
      setLoadingData(false);

      // Show success/error toast based on results
      if (results.length > 0) {
        toast.success(
          t('search.success.results', 'Search successful [{{count}}]', {
            count: results.length,
          })
        );
      } else {
        toast.error(t('search.errors.noMatches', 'Search found no matches'));
      }
    } catch (error) {
      // Clear loading state
      setLoadingData(false);
      console.error('Search error:', error);
      toast.error(t('search.errors.searchFailed', 'Search failed. Please try again.'));
    }
  };

  // Clear search and reset filters
  const handleClearSearch = () => {
    setInputValue('');
    setSearchTerm('');
    setManualSearchResults([]);

    // Reset filters by dispatching appropriate actions
    dispatch(clearFilterValues());
    dispatch(resetApplySearch());
    dispatch(setPriceFilter('all'));
    dispatch(setViewType('all'));

    // Close filter bubble if it's open
    if (isFilterBubbleOpen) {
      dispatch(toggleFilterBubble());
    }

    // Update URL to remove search term
    const params = new URLSearchParams(location.search);
    params.delete('term');
    const newUrl = params.toString()
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    window.history.pushState({}, '', newUrl);

    // Don't force a refetch since we want manual control
    // refetch();
  };

  // Filter saved searches based on search filter
  const filteredSavedSearches = savedSearches.filter((search) =>
    search.search_name.toLowerCase().includes(savedSearchFilter.toLowerCase())
  );

  // Handler for view type toggle
  const handleViewTypeChange = (
    e: React.MouseEvent<HTMLElement>,
    newValue: 'all' | 'instant' | 'products'
  ) => {
    if (newValue) {
      dispatch(setViewType(newValue));
    }
  };

  // Handler for price filter toggle
  const handlePriceFilterChange = (
    e: React.MouseEvent<HTMLElement>,
    newValue: 'all' | 'priced' | 'unpriced'
  ) => {
    if (newValue) {
      dispatch(setPriceFilter(newValue));
    }
  };

  return (
    <div>
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <Card className={classes.card}>
              <CardHeader color="primary" className={classes.cardHeader}>
                <h4>
                  {t('navigation.instantShop')} - {t('search.instantShop.title', 'Search')}
                </h4>
              </CardHeader>
              <CardBody>
                {/* Toolbar */}
                <Box className={classes.toolbar}>
                  {/* Tool icon */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip
                      title={t('search.item.toolsTooltip', 'These tools can be used on this page')}
                      placement="top"
                    >
                      <IconButton
                        size="small"
                        sx={{
                          color: '#FFD700',
                          '&:hover': { color: '#FFD700' },
                          fontSize: { xs: 16, md: 18 },
                          width: { xs: 28, md: 32 },
                          height: { xs: 28, md: 32 },
                          boxShadow: 0,
                        }}
                      >
                        <ToolsIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Main toolbar content - centered */}
                  <Box className={classes.toolbarContent}>
                    <Box className={classes.searchContainer}>
                      <TextField
                        value={inputValue}
                        onChange={handleSearchChange}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
                        placeholder={t('search.input.placeholder')}
                        variant="outlined"
                        size="small"
                        sx={{
                          minWidth: { xs: '100%', md: 220 },
                          maxWidth: { xs: '100%', md: 340 },
                          backgroundColor: (theme) => theme.palette.background.default,
                        }}
                        inputProps={{ 'aria-label': t('search.input.label'), autoComplete: 'off' }}
                        InputProps={{
                          endAdornment: inputValue && (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={t('search.input.clear')}
                                onClick={handleClearSearch}
                                edge="end"
                                size="small"
                                tabIndex={0}
                              >
                                <CloseIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      {/* Action buttons */}
                      <Box sx={{ display: 'flex', gap: 1, mt: { xs: 1, md: 0 } }}>
                        <Tooltip title={t('search.buttons.search')} placement="top">
                          <IconButton
                            color="primary"
                            onClick={handleSearchClick}
                            aria-label={t('search.buttons.search')}
                            sx={{
                              backgroundColor: (theme) => theme.palette.primary.main,
                              color: (theme) => theme.palette.primary.contrastText,
                              fontSize: { xs: 20, md: 24 },
                              width: { xs: 40, md: 44 },
                              height: { xs: 40, md: 44 },
                              border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                              '&:hover': {
                                backgroundColor: (theme) => theme.palette.primary.dark,
                                border: (theme) => `1px solid ${theme.palette.secondary.main}`,
                              },
                            }}
                          >
                            <SearchIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t('search.buttons.toggleFilters')} placement="top">
                          <IconButton
                            color={isFilterBubbleOpen ? 'warning' : 'primary'}
                            onClick={() => dispatch(toggleFilterBubble())}
                            aria-label={t('search.buttons.toggleFilters')}
                            sx={{
                              backgroundColor: isFilterBubbleOpen
                                ? (theme) => theme.palette.warning.main
                                : (theme) => theme.palette.background.default,
                              color: isFilterBubbleOpen
                                ? (theme) => theme.palette.warning.contrastText
                                : (theme) => theme.palette.text.primary,
                              fontSize: { xs: 16, md: 18 },
                              width: { xs: 28, md: 32 },
                              height: { xs: 28, md: 32 },
                              boxShadow: 0,
                              border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                              '&:hover': {
                                backgroundColor: isFilterBubbleOpen
                                  ? (theme) => theme.palette.warning.dark
                                  : (theme) => theme.palette.action.hover,
                                border: (theme) => `1px solid ${theme.palette.secondary.main}`,
                              },
                            }}
                          >
                            <FilterAltOutlined />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title={t('search.savedSearches', 'Saved Searches')}
                          placement="top"
                        >
                          <IconButton
                            ref={savedSearchesButtonRef}
                            aria-label={t('search.savedSearches', 'Saved Searches')}
                            onClick={() =>
                              setSavedSearchesAnchorEl(
                                savedSearchesAnchorEl ? null : savedSearchesButtonRef.current
                              )
                            }
                            sx={toolbarButtonSx}
                          >
                            <YoutubeSearchedForOutlined fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>

                  {/* Filter toggles on the right */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'column' },
                      gap: 0.5,
                      alignItems: { xs: 'center', md: 'flex-end' },
                      minWidth: { xs: 'auto', md: '300px' },
                      width: { xs: '100%', md: 'auto' },
                    }}
                  >
                    {/* View type toggle */}
                    <ToggleButtonGroup
                      value={viewType}
                      exclusive
                      onChange={handleViewTypeChange}
                      aria-label="view type"
                      size="small"
                      sx={{
                        backgroundColor: (theme) => theme.palette.background.default,
                        '& .MuiToggleButton-root': {
                          border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                          color: (theme) => theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1,
                          '&.Mui-selected': {
                            backgroundColor: (theme) => theme.palette.primary.main,
                            color: (theme) => theme.palette.primary.contrastText,
                            '&:hover': {
                              backgroundColor: (theme) => theme.palette.primary.dark,
                            },
                          },
                          '&:hover': {
                            backgroundColor: (theme) => theme.palette.action.hover,
                          },
                        },
                      }}
                    >
                      <Tooltip
                        title={t(
                          'search.viewType.allTooltip',
                          'View all items including both instant items and products'
                        )}
                      >
                        <ToggleButton value="all" aria-label="all">
                          <ShoppingCartCheckoutOutlined sx={{ mr: 0.5, fontSize: 14 }} />
                          <ShoppingCartOutlined sx={{ mr: 0, fontSize: 14 }} />
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              display: { xs: 'none', sm: 'inline' },
                            }}
                          >
                            {t('search.viewType.all', 'All')}
                          </Box>
                          <Box
                            sx={{
                              ml: 0.5,
                              px: 0.5,
                              py: 0,
                              borderRadius: 1,
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.1)'
                                  : 'rgba(0, 0, 0, 0.1)',
                              fontSize: '0.7rem',
                              minWidth: '20px',
                              textAlign: 'center',
                            }}
                          >
                            {groupedItems.length}
                          </Box>
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip
                        title={t(
                          'search.viewType.instantItemsTooltip',
                          'View only instant items that can be purchased immediately'
                        )}
                      >
                        <ToggleButton value="instant" aria-label="instant items">
                          <ShoppingCartCheckoutOutlined sx={{ fontSize: 14 }} />
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              display: { xs: 'none', sm: 'inline' },
                            }}
                          >
                            {t('search.viewType.instantItems', 'Instant Items')}
                          </Box>
                          <Box
                            sx={{
                              ml: 0.5,
                              px: 0.5,
                              py: 0,
                              borderRadius: 1,
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.1)'
                                  : 'rgba(0, 0, 0, 0.1)',
                              fontSize: '0.7rem',
                              minWidth: '20px',
                              textAlign: 'center',
                            }}
                          >
                            {groupedItems.filter((item) => !item.d2rshopproductlink).length}
                          </Box>
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip
                        title={t(
                          'search.viewType.productsTooltip',
                          'View only products that require additional processing or shipping'
                        )}
                      >
                        <ToggleButton value="products" aria-label="products">
                          <ShoppingCartOutlined sx={{ fontSize: 14 }} />
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              display: { xs: 'none', sm: 'inline' },
                            }}
                          >
                            {t('search.viewType.products', 'Products')}
                          </Box>
                          <Box
                            sx={{
                              ml: 0.5,
                              px: 0.5,
                              py: 0,
                              borderRadius: 1,
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.1)'
                                  : 'rgba(0, 0, 0, 0.1)',
                              fontSize: '0.7rem',
                              minWidth: '20px',
                              textAlign: 'center',
                            }}
                          >
                            {groupedItems.filter((item) => item.d2rshopproductlink).length}
                          </Box>
                        </ToggleButton>
                      </Tooltip>
                    </ToggleButtonGroup>

                    {/* Price filter toggle */}
                    <ToggleButtonGroup
                      value={priceFilter}
                      exclusive
                      onChange={handlePriceFilterChange}
                      aria-label="price filter"
                      size="small"
                      sx={{
                        backgroundColor: (theme) => theme.palette.background.default,
                        '& .MuiToggleButton-root': {
                          border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                          color: (theme) => theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1,
                          '&.Mui-selected': {
                            backgroundColor: (theme) => theme.palette.primary.main,
                            color: (theme) => theme.palette.primary.contrastText,
                            '&:hover': {
                              backgroundColor: (theme) => theme.palette.primary.dark,
                            },
                          },
                          '&:hover': {
                            backgroundColor: (theme) => theme.palette.action.hover,
                          },
                        },
                      }}
                    >
                      <Tooltip
                        title={t(
                          'search.priceFilter.allTooltip',
                          'View all items regardless of price'
                        )}
                      >
                        <ToggleButton value="all" aria-label="all prices">
                          <Box
                            component="span"
                            sx={{
                              display: { xs: 'inline', sm: 'inline' },
                            }}
                          >
                            {t('search.priceFilter.all', 'All')}
                          </Box>
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip
                        title={t(
                          'search.priceFilter.pricedTooltip',
                          'View only items with a price'
                        )}
                      >
                        <ToggleButton value="priced" aria-label="priced items">
                          <Box
                            component="span"
                            sx={{
                              display: { xs: 'inline', sm: 'inline' },
                            }}
                          >
                            {t('search.priceFilter.priced', 'Priced')}
                          </Box>
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip
                        title={t(
                          'search.priceFilter.unpricedTooltip',
                          'View only items without a price'
                        )}
                      >
                        <ToggleButton value="unpriced" aria-label="unpriced items">
                          <Box
                            component="span"
                            sx={{
                              display: { xs: 'inline', sm: 'inline' },
                            }}
                          >
                            {t('search.priceFilter.unpriced', 'Unpriced')}
                          </Box>
                        </ToggleButton>
                      </Tooltip>
                    </ToggleButtonGroup>
                  </Box>
                </Box>

                {/* Active Filters */}
                <ActiveFilters hideStatFilters={false} />

                {/* Main content */}
                <span>
                  <div>
                    {loadingData ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          my: 4,
                        }}
                      >
                        <CircularProgress
                          size={24}
                          sx={{ color: (theme) => theme.palette.primary.main }}
                        />
                      </Box>
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: 2,
                          }}
                        >
                          <PaginationControls
                            data={groupedItems}
                            currentPage={currentItemPage}
                            setCurrentPage={setCurrentItemPage}
                            itemsPerPage={itemsPerPage}
                            alwaysShow={true}
                          />
                        </Box>
                        <GridContainer spacing={3}>
                          {groupedItems
                            .filter((item) => {
                              // First apply view type filter
                              if (viewType === 'all') {
                                // No view type filter applied
                              } else if (viewType === 'instant' && item.d2rshopproductlink) {
                                return false;
                              } else if (viewType === 'products' && !item.d2rshopproductlink) {
                                return false;
                              }

                              // Then apply price filter
                              if (priceFilter === 'all') {
                                // No price filter applied
                              } else if (priceFilter === 'priced') {
                                // Check if item has a valid price
                                const hasPrice =
                                  typeof item.fiat_price === 'string' &&
                                  item.fiat_price !== '???' &&
                                  !isNaN(parseFloat(item.fiat_price));
                                if (!hasPrice) return false;
                              } else if (priceFilter === 'unpriced') {
                                // Check if item does not have a valid price
                                const hasPrice =
                                  typeof item.fiat_price === 'string' &&
                                  item.fiat_price !== '???' &&
                                  !isNaN(parseFloat(item.fiat_price));
                                if (hasPrice) return false;
                              }

                              return true;
                            })
                            .slice(
                              (currentItemPage - 1) * itemsPerPage,
                              currentItemPage * itemsPerPage
                            )
                            .map((item, key) => {
                              const groupKey = [
                                item.stats_description || '',
                                item.ocr_string || '',
                                item.rarity_id || '',
                                item.ladder || '',
                                item.hardcore || '',
                                item.skin_image_path || '',
                                item.fiat_price || '',
                                item.fg_price || '',
                                item.type_id || '',
                              ].join('|');
                              const maxStock = stockMap[groupKey] || 1;
                              return (
                                <GridItem xs={12} sm={6} md={3} key={key}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      padding: '8px',
                                      backgroundColor: 'rgba(0,0,0,0.05)',
                                      borderRadius: '8px',
                                      overflow: 'hidden',
                                      height: '100%',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px',
                                        padding: '4px',
                                        borderRadius: '4px',
                                        border: '1px solid rgba(0,0,0,0.1)',
                                        backgroundColor: 'rgba(0,0,0,0.02)',
                                        marginBottom: '8px',
                                      }}
                                    >
                                      <ItemActions
                                        item={{
                                          id: Number(item.id),
                                          original_rarity: item.original_rarity,
                                          d2rshopproductlink: item.d2rshopproductlink,
                                          context: item.context,
                                          status: 'not_in_cart',
                                          is_product: false,
                                        }}
                                        sessionToken={
                                          localStorage.getItem('session_token') || undefined
                                        }
                                        size="small"
                                        showViewDetails={true}
                                        showViewInShop={true}
                                        showAddToCart={false}
                                        showMakeOffer={true}
                                        showToolsIcon={true}
                                        onShare={(url) => {
                                          navigator.clipboard.writeText(url);
                                        }}
                                      />
                                    </div>

                                    {/* Quantity/cart row below toolbar */}
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: maxStock === 1 ? 'center' : 'flex-start',
                                        gap: 1.5,
                                        mt: 1,
                                        mb: 1,
                                        backgroundColor: (theme) =>
                                          theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(0,0,0,0.05)',
                                        border: (theme) =>
                                          `1px solid ${theme.palette.secondary.main}33`,
                                        borderRadius: '4px',
                                        padding: { xs: '2px 4px', sm: '4px 8px' },
                                        height: '40px', // Fixed height for consistent sizing
                                        width: '100%',
                                      }}
                                    >
                                      {maxStock === 1 ? (
                                        <Tooltip title={t('search.cart.addToCart', 'Add to cart')}>
                                          <IconButton
                                            color="secondary"
                                            onClick={() => handleAddToCart(item.id)}
                                            sx={{
                                              ml: 1,
                                              border: (theme) =>
                                                `1px solid ${theme.palette.secondary.main}33`,
                                              transition: 'border-color 0.2s, background 0.2s',
                                              '&:hover': {
                                                border: (theme) =>
                                                  `1px solid ${theme.palette.secondary.main}`,
                                                backgroundColor: (theme) =>
                                                  theme.palette.action.hover,
                                              },
                                            }}
                                          >
                                            <AddShoppingCartOutlined />
                                          </IconButton>
                                        </Tooltip>
                                      ) : (
                                        <>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => handleQuantityChange(item.id, -1)}
                                            disabled={(itemQuantities[item.id] || 1) <= 1}
                                            sx={{ minWidth: 28, minHeight: 28, px: 0 }}
                                          >
                                            -
                                          </Button>
                                          <TextField
                                            type="number"
                                            size="small"
                                            variant="standard"
                                            value={itemQuantities[item.id] || 1}
                                            onChange={(e) => {
                                              let val = Math.max(1, parseInt(e.target.value) || 1);
                                              if (val > maxStock) val = maxStock;
                                              setItemQuantities((q) => ({ ...q, [item.id]: val }));
                                            }}
                                            inputProps={{
                                              min: 1,
                                              max: maxStock,
                                              style: { textAlign: 'center', width: 40, padding: 0 },
                                            }}
                                            sx={{ mx: 1, width: 56 }}
                                          />
                                          <span
                                            style={{
                                              marginLeft: 4,
                                              color: '#888',
                                              fontSize: '0.95em',
                                            }}
                                          >
                                            / {maxStock}
                                          </span>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => handleQuantityChange(item.id, 1)}
                                            sx={{ minWidth: 28, minHeight: 28, px: 0 }}
                                            disabled={(itemQuantities[item.id] || 1) >= maxStock}
                                          >
                                            +
                                          </Button>
                                          <Tooltip
                                            title={t('search.cart.addToCart', 'Add to cart')}
                                          >
                                            <IconButton
                                              color="secondary"
                                              onClick={() => handleAddToCart(item.id)}
                                              sx={{
                                                ml: 1,
                                                border: (theme) =>
                                                  `1px solid ${theme.palette.secondary.main}33`,
                                                transition: 'border-color 0.2s, background 0.2s',
                                                '&:hover': {
                                                  border: (theme) =>
                                                    `1px solid ${theme.palette.secondary.main}`,
                                                  backgroundColor: (theme) =>
                                                    theme.palette.action.hover,
                                                },
                                              }}
                                            >
                                              <AddShoppingCartOutlined />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      )}
                                    </Box>

                                    <div
                                      style={{
                                        width: '100%',
                                        textAlign: 'center',
                                        marginBottom: '8px',
                                        height: '32px', // Fixed height for consistent sizing
                                      }}
                                    >
                                      {/* No longer displaying sharedLink and sharedItemId */}
                                    </div>

                                    <Box
                                      sx={{
                                        backgroundColor: (theme) =>
                                          theme.palette.mode === 'dark'
                                            ? theme.palette.grey[900]
                                            : theme.palette.grey[100],
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: (theme) =>
                                          `1px solid ${theme.palette.secondary.main}`,
                                        display: 'inline-flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        position: 'relative',
                                        width: '100%',
                                        height: '350px', // Fixed total height for the card content
                                      }}
                                    >
                                      {item?.context && item.context !== '' && (
                                        <Tooltip
                                          title={
                                            <div>
                                              {item.context.toLowerCase() === 'scl' &&
                                                'Softcore Ladder'}
                                              {item.context.toLowerCase() === 'scnl' &&
                                                'Softcore Non-Ladder'}
                                              {item.context.toLowerCase() === 'hcl' &&
                                                'Hardcore Ladder'}
                                              {item.context.toLowerCase() === 'hcnl' &&
                                                'Hardcore Non-Ladder'}
                                            </div>
                                          }
                                          arrow
                                        >
                                          <span
                                            style={{
                                              position: 'absolute',
                                              top: 8,
                                              left: 8,
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '2px',
                                              zIndex: 1,
                                            }}
                                          >
                                            {(item.context.toLowerCase() === 'scl' ||
                                              item.context.toLowerCase() === 'scnl') && (
                                              <img
                                                style={{ width: '24px' }}
                                                src={hardcoreOff}
                                                alt="hardcoreOff"
                                              />
                                            )}
                                            {(item.context.toLowerCase() === 'hcl' ||
                                              item.context.toLowerCase() === 'hcnl') && (
                                              <img
                                                style={{ width: '24px' }}
                                                src={hardcoreOn}
                                                alt="hardcoreOn"
                                              />
                                            )}
                                            {(item.context.toLowerCase() === 'scl' ||
                                              item.context.toLowerCase() === 'hcl') && (
                                              <img
                                                style={{ width: '24px' }}
                                                src={ladderOn}
                                                alt="ladderOn"
                                              />
                                            )}
                                            {(item.context.toLowerCase() === 'scnl' ||
                                              item.context.toLowerCase() === 'hcnl') && (
                                              <img
                                                style={{ width: '24px' }}
                                                src={ladderOff}
                                                alt="ladderOff"
                                              />
                                            )}
                                          </span>
                                        </Tooltip>
                                      )}

                                      {item.d2rshopproductlink !== '' && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            backgroundColor: (theme) => theme.palette.primary.main,
                                            color: 'white',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '0.875rem',
                                            fontWeight: 'bold',
                                            zIndex: 1,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                          }}
                                        >
                                          {typeof item.fiat_price === 'string' &&
                                          item.fiat_price !== '???' &&
                                          !isNaN(parseFloat(item.fiat_price))
                                            ? `${parseFloat(item.fiat_price).toFixed(2).replaceAll('.00', '')}$`
                                            : '???$'}
                                        </Box>
                                      )}

                                      <Box
                                        sx={{
                                          width: '100%',
                                          height: '200px', // Fixed height for image container
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        <Tooltip
                                          title={
                                            <img
                                              src={`https://d2r-member.com/image/${item.product_image_path || ''}`}
                                              alt={item.title || item.name || ''}
                                              style={{
                                                objectFit: 'contain',
                                                maxWidth: '100%',
                                                maxHeight: '300px',
                                              }}
                                            />
                                          }
                                          arrow
                                        >
                                          <Button
                                            component="div"
                                            onClick={() =>
                                              window.open(`/item/${item.id}`, '_blank')
                                            }
                                            sx={{
                                              p: 0,
                                              m: 0,
                                              minWidth: 'auto',
                                              width: '100%',
                                              height: '100%',
                                              display: 'flex',
                                              justifyContent: 'center',
                                              alignItems: 'center',
                                              transition: 'background-color 0.3s ease',
                                              '&:hover': {
                                                backgroundColor: (theme) =>
                                                  `${theme.palette.primary.main}30`, // Primary color with 30% opacity
                                              },
                                            }}
                                          >
                                            <img
                                              src={`https://d2r-member.com/skin/${item.skin_image_path || ''}`}
                                              alt={item.name || ''}
                                              style={{
                                                maxWidth: '100%',
                                                maxHeight: '200px',
                                                objectFit: 'scale-down',
                                                cursor: 'pointer',
                                              }}
                                            />
                                          </Button>
                                        </Tooltip>
                                      </Box>

                                      <Box
                                        sx={{
                                          p: 2,
                                          whiteSpace: 'pre-wrap',
                                          fontFamily: 'monospace',
                                          color: '#000',
                                          position: 'relative',
                                          borderTop: (theme) =>
                                            `1px solid ${theme.palette.secondary.main}`,
                                          backgroundColor: (theme) =>
                                            theme.palette.mode === 'dark'
                                              ? theme.palette.grey[900]
                                              : theme.palette.grey[100],
                                          width: '100%',
                                          height: '150px', // Fixed height for stats container
                                          overflow: 'auto',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          justifyContent: 'flex-start',
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          component="pre"
                                          sx={{
                                            margin: 0,
                                            fontFamily: 'monospace',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            color: '#000',
                                            textAlign: 'center',
                                            width: '100%',
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              gap: 1,
                                              mb: 1,
                                            }}
                                          >
                                            <Tooltip
                                              title={item.original_rarity || 'Normal'}
                                              placement="top"
                                            >
                                              <Box
                                                sx={{
                                                  width: '12px',
                                                  height: '12px',
                                                  minWidth: '12px',
                                                  minHeight: '12px',
                                                  borderRadius: '50%',
                                                  backgroundColor: getColorFromRarity(
                                                    item.original_rarity
                                                  ),
                                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                  flexShrink: 0,
                                                }}
                                              />
                                            </Tooltip>
                                            {item.name}
                                          </Box>
                                          {item.stats_description}
                                        </Typography>
                                        <Tooltip
                                          title={t('common.copyToClipboard')}
                                          placement="top"
                                        >
                                          <IconButton
                                            onClick={() => {
                                              navigator.clipboard.writeText(
                                                item.name
                                                  ? `${item.name}\n\n${item.stats_description}`
                                                  : item.stats_description || ''
                                              );
                                              toast.success(t('common.copiedToClipboard'));
                                            }}
                                            size="small"
                                            sx={{
                                              position: 'absolute',
                                              top: 8,
                                              right: 8,
                                              color: (theme) => theme.palette.text.secondary,
                                            }}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Box>
                                  </div>
                                </GridItem>
                              );
                            })}
                        </GridContainer>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mt: 2,
                          }}
                        >
                          <PaginationControls
                            data={groupedItems}
                            currentPage={currentItemPage}
                            setCurrentPage={setCurrentItemPage}
                            itemsPerPage={itemsPerPage}
                            alwaysShow={true}
                          />
                        </Box>
                      </>
                    )}
                  </div>
                </span>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('common.share')}
          <IconButton
            aria-label="close"
            onClick={() => setShareDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              p: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              color: (theme) => theme.palette.text.primary,
              position: 'relative',
            }}
          >
            {/* No longer displaying sharedLink and sharedItemId */}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Help Bubble */}
      <BubbleContainer pageKey="publicPage" helpText={t('help.publicPage')} />

      {/* Saved Searches Popover */}
      <Popover
        className={classes.savedSearchesPopover}
        open={isSavedSearchesOpen}
        anchorEl={savedSearchesAnchorEl}
        onClose={handleSavedSearchesClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            backgroundColor: (theme) => theme.palette.background.default,
            border: (theme) => `1px solid ${theme.palette.secondary.main}40`,
            borderRadius: '8px',
            boxShadow: (theme) =>
              `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${theme.palette.secondary.main}40`,
            p: 2,
            minWidth: 320,
            maxWidth: 400,
            width: '100%',
            marginTop: '8px',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            value={savedSearchFilter}
            onChange={(e) => setSavedSearchFilter(e.target.value)}
            placeholder={t('filters.searchSavedSearches')}
            size="small"
            variant="outlined"
            fullWidth
            sx={{ mb: 1 }}
          />
          {filteredSavedSearches.length > 0 ? (
            filteredSavedSearches.map((search) => (
              <Button
                key={search.search_name}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  textTransform: 'none',
                  border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                  '&:hover': {
                    border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: '8px 16px',
                }}
                onClick={() => {
                  handleSavedSearchClick(search.search_term);
                }}
              >
                <Typography variant="body2" sx={{ color: 'inherit' }}>
                  {search.search_count}x
                </Typography>
                <Typography variant="body2" sx={{ color: 'inherit', flex: 1, textAlign: 'center' }}>
                  {search.search_name}
                </Typography>
              </Button>
            ))
          ) : (
            <Button
              variant="outlined"
              size="small"
              href="/saved-searches"
              target="_blank"
              fullWidth
              sx={{
                textTransform: 'none',
                border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
                color: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                '&:hover': {
                  border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                },
              }}
            >
              {t('filters.createSavedSearches')}
            </Button>
          )}
        </Box>
      </Popover>
    </div>
  );
});
